// =============================================================================
// Migrazione JSON · Dashboard admin (Sprint 3 · IMPORT REALE)
// =============================================================================
// Percorso: /admin/migrazione-json
// Flusso:
//   1. Upload JSON + manifest → job in 'previewed'
//   2. Analizza → status 'analyzing' → 'ready' con items pending
//   3. Preview + Diff table (filtrabile)
//   4. Conferma esplicita → Avvia import (background) → status 'importing'
//   5. Progress bar con polling ogni 2s → 'completed' | 'completed_with_errors'
//   6. Retry errori / cancel / download report JSON
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import {
  ArrowLeft,
  Database,
  FileJson,
  FileText,
  Loader2,
  RefreshCw,
  Trash2,
  Upload,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  Play,
  RotateCcw,
  Ban,
  Download,
  Sparkles,
  Filter,
  X,
} from "lucide-react";

// ---------- utils ----------------------------------------------------------
const fmtBytes = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) { return iso; }
};

const STATUS_META = {
  uploaded: { bg: "bg-slate-100", fg: "text-slate-700", label: "Caricato" },
  previewed: { bg: "bg-blue-50", fg: "text-blue-700", label: "Preview pronta" },
  analyzing: { bg: "bg-indigo-50", fg: "text-indigo-700", label: "Analisi in corso" },
  ready: { bg: "bg-amber-50", fg: "text-amber-700", label: "Pronto per import" },
  importing: { bg: "bg-emerald-50", fg: "text-emerald-700", label: "Import in corso" },
  completed: { bg: "bg-emerald-50", fg: "text-emerald-700", label: "Completato" },
  completed_with_errors: { bg: "bg-amber-50", fg: "text-amber-700", label: "Completato con errori" },
  failed: { bg: "bg-red-50", fg: "text-red-700", label: "Errore" },
  cancelled: { bg: "bg-slate-100", fg: "text-slate-500", label: "Annullato" },
};

const StatusPill = ({ status }) => {
  const s = STATUS_META[status] || { bg: "bg-slate-100", fg: "text-slate-700", label: status || "—" };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs ${s.bg} ${s.fg}`} data-testid={`status-pill-${status}`}>
      {s.label}
    </span>
  );
};

const ActionBadge = ({ action }) => {
  const map = {
    create: { bg: "bg-emerald-100", fg: "text-emerald-800", label: "CREATE" },
    update: { bg: "bg-blue-100", fg: "text-blue-800", label: "UPDATE" },
    skip: { bg: "bg-slate-100", fg: "text-slate-700", label: "SKIP" },
    error: { bg: "bg-red-100", fg: "text-red-800", label: "ERROR" },
    conflict: { bg: "bg-orange-100", fg: "text-orange-800", label: "CONFLICT" },
    pending: { bg: "bg-slate-100", fg: "text-slate-500", label: "PENDING" },
  };
  const s = map[action] || map.pending;
  return <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold tracking-wide ${s.bg} ${s.fg}`}>{s.label}</span>;
};

// ---------- Drop zone (unchanged) ------------------------------------------
const DropZone = ({ label, accept, file, onFile, testId }) => {
  const inputRef = useRef(null);
  return (
    <div
      className="border-2 border-dashed border-[#E5E7EB] rounded-2xl p-5 flex items-center justify-between gap-4 bg-white hover:border-[#12964A]/60 transition-colors"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 min-w-0">
        {label === "JSON principale" ? (
          <FileJson className="w-5 h-5 text-[#12964A] shrink-0" />
        ) : (
          <FileText className="w-5 h-5 text-slate-400 shrink-0" />
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#1F2937]">{label}</div>
          <div className="text-xs text-slate-500 truncate">
            {file ? `${file.name} · ${fmtBytes(file.size)}` : "Nessun file selezionato"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {file && (
          <button type="button" onClick={() => onFile(null)} className="text-xs text-slate-500 hover:text-red-600" data-testid={`${testId}-clear`}>
            Rimuovi
          </button>
        )}
        <button type="button" onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-1.5 border border-[#E5E7EB] hover:border-slate-400 rounded-full px-3 py-1.5 text-xs" data-testid={`${testId}-select`}>
          <Upload className="w-3.5 h-3.5" /> Seleziona
        </button>
        <input ref={inputRef} type="file" accept={accept} className="hidden"
          onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid={`${testId}-input`} />
      </div>
    </div>
  );
};

// ---------- Confirm modal --------------------------------------------------
const ConfirmModal = ({ open, onClose, onConfirm, title, message, confirmLabel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="confirm-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4">
        <h3 className="font-display font-bold text-xl text-[#1F2937]">{title}</h3>
        <p className="text-sm text-slate-600 whitespace-pre-line">{message}</p>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-[#E5E7EB] text-sm hover:bg-slate-50" data-testid="confirm-cancel">
            Annulla
          </button>
          <button type="button" onClick={onConfirm} className="px-4 py-2 rounded-full bg-[#12964A] text-white text-sm hover:bg-[#0E7C3D]" data-testid="confirm-ok">
            {confirmLabel || "Conferma"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Item filters helper -------------------------------------------
const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "create", label: "CREATE" },
  { key: "update", label: "UPDATE" },
  { key: "conflict", label: "CONFLICT" },
  { key: "skip", label: "SKIP" },
  { key: "error", label: "Errori" },
  { key: "warnings", label: "Con warning" },
  { key: "no_logo", label: "Senza logo" },
  { key: "no_cover", label: "Senza cover" },
  { key: "no_category", label: "Senza categoria" },
  { key: "suspect_location", label: "Indirizzo sospetto" },
];

const itemMatches = (item, key) => {
  const warns = item.warnings || [];
  const media = item.media_summary || {};
  const hasWarn = (needle) => warns.some(w => (w || "").includes(needle));
  switch (key) {
    case "all": return true;
    case "create": return item.action === "create";
    case "update": return item.action === "update";
    case "conflict": return item.action === "conflict";
    case "skip": return item.action === "skip";
    case "error": return item.status === "error" || item.action === "error";
    case "warnings": return warns.length > 0;
    case "no_logo": return !media.has_logo;
    case "no_cover": return !media.has_cover;
    case "no_category": return hasWarn("no_category") || hasWarn("category_not_matched");
    case "suspect_location": return hasWarn("invalid_location") || hasWarn("coordinates_out_of_range");
    default: return true;
  }
};

// ---------- Preview + Diff table ------------------------------------------
const DiffTable = ({ items, onResolveConflict }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [resolving, setResolving] = useState(null);
  return (
    <div className="border border-[#E5E7EB] rounded-2xl overflow-hidden bg-white">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="diff-table">
          <thead className="bg-[#F8F9FA] text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Azione</th>
              <th className="text-left p-3">Titolo</th>
              <th className="text-left p-3">Slug</th>
              <th className="text-center p-3">Diff</th>
              <th className="text-center p-3">Logo</th>
              <th className="text-center p-3">Cover</th>
              <th className="text-center p-3">Gallery</th>
              <th className="text-left p-3">Warning</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const media = it.media_summary || {};
              const warns = it.warnings || [];
              const diffKeys = it.diff ? Object.keys(it.diff) : [];
              const isOpen = expandedId === it.id;
              return (
                <React.Fragment key={it.id}>
                  <tr className={`border-t border-[#F1F5F9] hover:bg-slate-50 cursor-pointer ${it.action === "conflict" ? "bg-orange-50/40" : ""}`} onClick={() => setExpandedId(isOpen ? null : it.id)} data-testid={`item-row-${it.external_id}`}>
                    <td className="p-3"><ActionBadge action={it.action} /></td>
                    <td className="p-3 font-medium text-[#1F2937] max-w-[260px] truncate" title={it.title}>{it.title || "—"}</td>
                    <td className="p-3 text-xs text-slate-500 font-mono max-w-[180px] truncate">{it.slug || "—"}</td>
                    <td className="p-3 text-center">
                      {diffKeys.length > 0 ? (
                        <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 text-xs">{diffKeys.length}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="p-3 text-center">{media.has_logo ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mx-auto" /> : <X className="w-4 h-4 text-slate-300 mx-auto" />}</td>
                    <td className="p-3 text-center">{media.has_cover ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mx-auto" /> : <X className="w-4 h-4 text-slate-300 mx-auto" />}</td>
                    <td className="p-3 text-center text-xs text-slate-500">{media.gallery_count || 0}</td>
                    <td className="p-3">
                      {warns.length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                          <AlertTriangle className="w-3 h-3" /> {warns.length}
                        </span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-[#FAFBFC]">
                      <td colSpan={8} className="p-4 border-t border-[#F1F5F9]">
                        {it.action === "conflict" && (
                          <div className="mb-3 rounded-xl bg-orange-50 border border-orange-200 px-3 py-2 text-sm text-orange-900">
                            <div className="font-semibold mb-1">Conflitto: questo record è stato modificato manualmente dopo un import precedente.</div>
                            <div className="text-xs mb-2">
                              WordPress vuole cambiare {diffKeys.length} campi. Nessuna sovrascrittura automatica verrà applicata.
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                disabled={resolving === it.id}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`Forza aggiornamento di "${it.title}"? I dati WordPress sovrascriveranno le modifiche manuali. human_reviewed_at verrà resettato.`)) return;
                                  setResolving(it.id);
                                  await onResolveConflict?.(it.id, "force_update");
                                  setResolving(null);
                                }}
                                className="inline-flex items-center gap-1.5 rounded-full bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 text-xs disabled:opacity-50"
                                data-testid={`resolve-force-${it.external_id}`}
                              >
                                Forza aggiornamento (usa WP)
                              </button>
                              <button
                                type="button"
                                disabled={resolving === it.id}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setResolving(it.id);
                                  await onResolveConflict?.(it.id, "keep_manual");
                                  setResolving(null);
                                }}
                                className="inline-flex items-center gap-1.5 rounded-full border border-orange-300 text-orange-700 hover:bg-orange-100 px-3 py-1.5 text-xs disabled:opacity-50"
                                data-testid={`resolve-keep-${it.external_id}`}
                              >
                                Mantieni versione manuale
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Diff ({diffKeys.length})</div>
                            {diffKeys.length === 0 ? (
                              <div className="text-xs text-slate-400 italic">Nessuna modifica</div>
                            ) : (
                              <pre className="text-xs bg-white border border-[#E5E7EB] rounded-lg p-2 overflow-x-auto max-h-64">{JSON.stringify(it.diff, null, 2)}</pre>
                            )}
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Warning</div>
                            {warns.length === 0 ? (
                              <div className="text-xs text-slate-400 italic">Nessun warning</div>
                            ) : (
                              <ul className="text-xs text-amber-800 space-y-1">
                                {warns.map((w, i) => <li key={i} className="font-mono">• {w}</li>)}
                              </ul>
                            )}
                            {it.errors && it.errors.length > 0 && (
                              <div className="mt-3">
                                <div className="text-xs uppercase tracking-wider text-red-600 mb-1">Errori</div>
                                <ul className="text-xs text-red-700 space-y-1">
                                  {it.errors.map((e, i) => <li key={i} className="font-mono">• {e}</li>)}
                                </ul>
                              </div>
                            )}
                            {it.media_summary && (
                              <div className="mt-3">
                                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Media</div>
                                <div className="text-xs text-slate-600 space-y-0.5">
                                  {it.media_summary.logo_url && <div className="truncate">Logo: <span className="font-mono">{it.media_summary.logo_url}</span></div>}
                                  {it.media_summary.cover_url && <div className="truncate">Cover: <span className="font-mono">{it.media_summary.cover_url}</span></div>}
                                  <div>Gallery items: <b>{it.media_summary.gallery_count || 0}</b></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Progress bar --------------------------------------------------
const ProgressBar = ({ job }) => {
  const total = job.records_total || 0;
  const done = (job.records_created || 0) + (job.records_updated || 0) + (job.records_skipped || 0) + (job.records_errors || 0);
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className="space-y-2" data-testid="import-progress">
      <div className="flex items-center justify-between text-xs">
        <div className="font-medium text-[#1F2937]">
          {job.current_record_title ? <>Elaboro <span className="text-slate-500">{job.current_record_title}</span></> : "In coda…"}
        </div>
        <div className="text-slate-500">{done} / {total} · {pct}%</div>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#12964A] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-4 gap-2 text-xs">
        <div className="bg-emerald-50 text-emerald-800 rounded-lg px-2 py-1">CREATE <b>{job.records_created || 0}</b></div>
        <div className="bg-blue-50 text-blue-800 rounded-lg px-2 py-1">UPDATE <b>{job.records_updated || 0}</b></div>
        <div className="bg-slate-50 text-slate-700 rounded-lg px-2 py-1">SKIP <b>{job.records_skipped || 0}</b></div>
        <div className="bg-red-50 text-red-800 rounded-lg px-2 py-1">ERROR <b>{job.records_errors || 0}</b></div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs pt-1">
        <div className="bg-emerald-50 text-emerald-800 rounded-lg px-2 py-1">Media OK <b>{job.media_imported || 0}</b></div>
        <div className="bg-slate-50 text-slate-700 rounded-lg px-2 py-1">Media dup. <b>{job.media_skipped || 0}</b></div>
        <div className="bg-red-50 text-red-800 rounded-lg px-2 py-1">Media err. <b>{job.media_errors || 0}</b></div>
      </div>
    </div>
  );
};

// ---------- Job detail panel ----------------------------------------------
const JobDetail = ({ job, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [filter, setFilter] = useState("all");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(null); // 'analyzing' | 'importing' | 'retrying' | 'cancelling' | null
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const loadItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const res = await adminApi.listMigrationItems(job.id, { limit: 1000 });
      setItems(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Impossibile caricare gli item.");
    } finally { setLoadingItems(false); }
  }, [job.id]);

  useEffect(() => {
    if (["ready", "importing", "completed", "completed_with_errors", "failed", "cancelled"].includes(job.status)) {
      loadItems();
    } else {
      setItems([]);
    }
  }, [job.id, job.status, loadItems]);

  const filtered = useMemo(() => items.filter(it => itemMatches(it, filter)), [items, filter]);
  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const f of FILTERS) if (f.key !== "all") c[f.key] = items.filter(it => itemMatches(it, f.key)).length;
    return c;
  }, [items]);

  const handleAnalyze = async () => {
    setBusy("analyzing"); setErr(null); setMsg(null);
    try {
      await adminApi.analyzeMigration(job.id);
      setMsg("Analisi completata.");
      await onChanged?.();
      await loadItems();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore durante l'analisi.");
    } finally { setBusy(null); }
  };

  const handleStartImport = async () => {
    setConfirmOpen(false);
    setBusy("importing"); setErr(null); setMsg(null);
    try {
      await adminApi.startMigrationImport(job.id);
      setMsg("Import avviato in background.");
      await onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore avvio import.");
    } finally { setBusy(null); }
  };

  const handleRetry = async () => {
    setBusy("retrying"); setErr(null); setMsg(null);
    try {
      const res = await adminApi.retryMigrationErrors(job.id);
      setMsg(`Rilancio in corso: ${res.data.items_queued || 0} item.`);
      await onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore retry.");
    } finally { setBusy(null); }
  };

  const handleCancel = async () => {
    setBusy("cancelling");
    try {
      await adminApi.cancelMigration(job.id);
      setMsg("Cancellazione richiesta.");
      await onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore cancel.");
    } finally { setBusy(null); }
  };

  const handleDownloadReport = async () => {
    try {
      const res = await adminApi.downloadMigrationReport(job.id);
      const blob = new Blob([res.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `migration-report-${job.id.slice(0, 8)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr("Errore download report.");
    }
  };

  const handleResolveConflict = useCallback(async (itemId, resolution) => {
    try {
      await adminApi.resolveMigrationConflict(job.id, itemId, resolution);
      await loadItems();
      await onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore risoluzione conflitto.");
    }
  }, [job.id, loadItems, onChanged]);

  const canAnalyze = ["previewed", "uploaded", "ready", "completed_with_errors", "failed", "cancelled"].includes(job.status);
  const canImport = job.status === "ready" && (counts.create > 0 || counts.update > 0);
  const canRetry = (job.status === "completed_with_errors" || job.status === "failed") && (job.records_errors || 0) > 0;
  const canCancel = ["previewed", "ready", "importing"].includes(job.status);

  return (
    <div className="px-4 pb-4 pt-1 border-t border-[#F1F5F9] space-y-4">
      {/* Meta */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div><div className="text-slate-500">Dimensione</div><div className="text-[#1F2937]">{fmtBytes(job.file_size_bytes)}</div></div>
        <div><div className="text-slate-500">SHA-256</div><div className="text-[#1F2937] font-mono truncate" title={job.file_sha256}>{job.file_sha256 ? `${job.file_sha256.slice(0,12)}…` : "—"}</div></div>
        <div><div className="text-slate-500">Caricato da</div><div className="text-[#1F2937] truncate">{job.created_by || "—"}</div></div>
        <div><div className="text-slate-500">Job ID</div><div className="text-[#1F2937] font-mono truncate" title={job.id}>{job.id.slice(0,8)}…</div></div>
      </div>

      {/* Progress / analyze summary */}
      {job.status === "importing" && <ProgressBar job={job} />}

      {job.status === "ready" && job.analyze_summary && (
        <div className="rounded-2xl bg-[#F0FAF3] border border-emerald-200 px-4 py-3 text-sm text-emerald-900 space-y-1">
          <div className="flex items-center gap-2 font-semibold"><Sparkles className="w-4 h-4" /> Analisi pronta</div>
          <div>
            <b>{job.analyze_summary.total || 0}</b> record ·{" "}
            <b>{job.analyze_summary.planned_create || 0}</b> CREATE ·{" "}
            <b>{job.analyze_summary.planned_update || 0}</b> UPDATE ·{" "}
            <b>{job.analyze_summary.planned_skip || 0}</b> SKIP ·{" "}
            {(job.analyze_summary.planned_conflict || 0) > 0 && (
              <><span className="text-orange-700 font-semibold">{job.analyze_summary.planned_conflict} CONFLICT</span> · </>
            )}
            <b>{job.analyze_summary.planned_error || 0}</b> ERROR ·{" "}
            <b>{job.analyze_summary.with_warnings || 0}</b> con warning
          </div>
          {(job.analyze_summary.planned_conflict || 0) > 0 && (
            <div className="text-xs text-orange-800 pt-1">
              I record CONFLICT non verranno toccati automaticamente. Filtrali con il pulsante &quot;CONFLICT&quot; e decidi per ciascuno.
            </div>
          )}
        </div>
      )}

      {(job.status === "completed" || job.status === "completed_with_errors") && job.result_summary && (
        <div className={`rounded-2xl px-4 py-3 text-sm ${job.status === "completed" ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-amber-50 border border-amber-200 text-amber-900"}`}>
          <div className="font-semibold mb-1">Report finale</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div>Creati: <b>{job.result_summary.created || 0}</b></div>
            <div>Aggiornati: <b>{job.result_summary.updated || 0}</b></div>
            <div>Saltati: <b>{job.result_summary.skipped || 0}</b></div>
            <div>Errori: <b>{job.result_summary.errors || 0}</b></div>
            <div>Media importati: <b>{job.result_summary.media_imported || 0}</b></div>
            <div>Media duplicati: <b>{job.result_summary.media_skipped || 0}</b></div>
            <div>Media errori: <b>{job.result_summary.media_errors || 0}</b></div>
          </div>
        </div>
      )}

      {msg && <div className="text-sm bg-blue-50 border border-blue-200 text-blue-800 rounded-xl px-4 py-2" data-testid="job-msg">{msg}</div>}
      {err && <div className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-2" data-testid="job-err">{err}</div>}
      {job.log_summary && <div className="text-xs text-slate-500 italic">{job.log_summary}</div>}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={!canAnalyze || busy} onClick={handleAnalyze} className="inline-flex items-center gap-2 border border-[#12964A] text-[#12964A] hover:bg-[#12964A] hover:text-white rounded-full px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-testid="btn-analyze">
          {busy === "analyzing" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Analizza importazione
        </button>
        <button type="button" disabled={!canImport || busy} onClick={() => setConfirmOpen(true)} className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-testid="btn-start-import">
          <Play className="w-4 h-4" /> Avvia importazione
        </button>
        <button type="button" disabled={!canRetry || busy} onClick={handleRetry} className="inline-flex items-center gap-2 border border-amber-500 text-amber-700 hover:bg-amber-50 rounded-full px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-testid="btn-retry">
          <RotateCcw className="w-4 h-4" /> Riprova errori
        </button>
        <button type="button" disabled={!canCancel || busy} onClick={handleCancel} className="inline-flex items-center gap-2 border border-[#E5E7EB] text-slate-600 hover:bg-slate-50 rounded-full px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-testid="btn-cancel-import">
          <Ban className="w-4 h-4" /> Annulla job
        </button>
        {items.length > 0 && (
          <button type="button" onClick={handleDownloadReport} className="inline-flex items-center gap-2 border border-[#E5E7EB] text-slate-700 hover:bg-slate-50 rounded-full px-4 py-2 text-sm ml-auto" data-testid="btn-download-report">
            <Download className="w-4 h-4" /> Scarica report JSON
          </button>
        )}
      </div>

      {/* Diff table + filters */}
      {items.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-slate-400" />
            {FILTERS.map(f => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)} className={`text-xs px-3 py-1 rounded-full transition-colors ${filter === f.key ? "bg-[#12964A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`filter-${f.key}`}>
                {f.label} <span className="opacity-70">({counts[f.key] || 0})</span>
              </button>
            ))}
          </div>
          {loadingItems ? (
            <div className="p-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">Nessun item corrisponde al filtro selezionato.</div>
          ) : (
            <DiffTable items={filtered} onResolveConflict={handleResolveConflict} />
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleStartImport}
        title="Confermi l'avvio dell'importazione?"
        message={"Stai per creare o aggiornare attività reali nel database.\nL'operazione è idempotente, ma può modificare dati esistenti provenienti da WordPress.\n\nProcedere?"}
        confirmLabel="Sì, avvia import"
      />
    </div>
  );
};

// ---------- Job row (list) ------------------------------------------------
const JobRow = ({ job, expanded, onToggle, onDelete, onChanged }) => {
  return (
    <div className="border border-[#E5E7EB] rounded-2xl bg-white overflow-hidden">
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F8F9FA] text-left" data-testid={`job-row-${job.id}`}>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <FileJson className="w-4 h-4 text-[#12964A] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#1F2937] truncate">
            {job.filename || "—"}
            {job.manifest_filename && <span className="ml-2 text-xs text-slate-500">+ {job.manifest_filename}</span>}
          </div>
          <div className="text-xs text-slate-500 truncate">
            {job.source_label || job.source_type} · {fmtDate(job.created_at)}
            {job.export_id && <> · export: <code className="font-mono">{String(job.export_id).slice(0, 8)}…</code></>}
          </div>
        </div>
        <div className="hidden md:flex items-center gap-4 text-xs">
          <div className="text-slate-500">Totale <b className="text-[#1F2937]">{job.records_total || 0}</b></div>
          <div className="text-emerald-700">✓ <b>{(job.records_created || 0) + (job.records_updated || 0)}</b></div>
          <div className="text-red-700">✗ <b>{job.records_errors || 0}</b></div>
        </div>
        <StatusPill status={job.status} />
      </button>
      {expanded && <JobDetail job={job} onChanged={onChanged} />}
      {expanded && (
        <div className="px-4 pb-3 flex justify-end">
          <button type="button" onClick={() => onDelete(job)} className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700" data-testid={`job-delete-${job.id}`}>
            <Trash2 className="w-3.5 h-3.5" /> Elimina job
          </button>
        </div>
      )}
    </div>
  );
};

// ---------- MAIN ----------------------------------------------------------
const MigrazioneJson = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [jsonFile, setJsonFile] = useState(null);
  const [manifestFile, setManifestFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await adminApi.listMigrations();
      setJobs(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossibile caricare i job di migrazione.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Polling automatico se c'è un job in importing / analyzing
  useEffect(() => {
    const activeIds = jobs.filter(j => ["importing", "analyzing"].includes(j.status)).map(j => j.id);
    if (activeIds.length === 0) return;
    const iv = setInterval(load, 2500);
    return () => clearInterval(iv);
  }, [jobs, load]);

  const canUpload = !!jsonFile && !uploading;

  const handleUpload = useCallback(async () => {
    if (!jsonFile) return;
    setUploading(true); setUploadError(null); setLastResult(null);
    try {
      const res = await adminApi.previewMigration(jsonFile, manifestFile);
      setLastResult(res.data);
      setJsonFile(null); setManifestFile(null);
      await load();
      if (res.data?.job?.id) setExpandedId(res.data.job.id);
    } catch (err) {
      setUploadError(err?.response?.data?.detail || "Errore durante il caricamento.");
    } finally { setUploading(false); }
  }, [jsonFile, manifestFile, load]);

  const handleDelete = useCallback(async (job) => {
    if (!window.confirm(`Eliminare definitivamente il job di ${job.filename || "—"}?`)) return;
    try {
      await adminApi.deleteMigration(job.id);
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore eliminazione job.");
    }
  }, []);

  const totals = useMemo(() => {
    return jobs.reduce((acc, j) => ({
      jobs: acc.jobs + 1,
      records: acc.records + (j.records_total || 0),
      imported: acc.imported + (j.records_created || 0) + (j.records_updated || 0),
    }), { jobs: 0, records: 0, imported: 0 });
  }, [jobs]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#12964A]" data-testid="link-back-admin">
              <ArrowLeft className="w-4 h-4" /> Amministrazione
            </Link>
            <div className="h-6 w-px bg-[#E5E7EB]" />
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-[#12964A]" />
              <div>
                <div className="text-sm font-display font-bold text-[#1F2937]">Migrazione JSON</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">WordPress import</div>
              </div>
            </div>
          </div>
          <button type="button" onClick={load} className="inline-flex items-center gap-2 border border-[#E5E7EB] hover:border-slate-400 rounded-full px-4 py-1.5 text-sm" data-testid="btn-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 space-y-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Sprint 3 · Migrazione</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">Importa JSON da WordPress</h1>
          <p className="text-slate-500 mt-2 max-w-3xl">
            Carica il file <code className="font-mono text-sm">businesses-export.json</code> generato da{" "}
            <code className="font-mono text-sm">wp_exporter.php</code> insieme al{" "}
            <code className="font-mono text-sm">migration-manifest.json</code> (opzionale).
            Dopo la preview, esegui l&apos;<b>analisi</b> per vedere quali record verranno creati/aggiornati e infine l&apos;<b>import reale</b>.
          </p>
        </div>

        <div className="rounded-2xl border border-blue-200 bg-blue-50 text-blue-900 px-4 py-3 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="text-sm">
            <b>Flusso in 3 step:</b> 1) <i>Analizza importazione</i> per vedere il diff · 2) <i>Avvia importazione</i> (conferma esplicita) · 3) monitora il progress e scarica il report finale.
            <br />L&apos;import è <b>idempotente</b>: riavviarlo non crea duplicati (chiave <code className="font-mono">external_source + external_id</code>).
          </div>
        </div>

        {/* Upload */}
        <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 space-y-4" data-testid="upload-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-xl text-[#1F2937]">Nuovo upload</h2>
            <div className="text-xs text-slate-500">Formati accettati: <code className="font-mono">.json</code></div>
          </div>
          <DropZone label="JSON principale" accept="application/json,.json" file={jsonFile} onFile={setJsonFile} testId="drop-json" />
          <DropZone label="Manifest (opzionale)" accept="application/json,.json" file={manifestFile} onFile={setManifestFile} testId="drop-manifest" />

          {uploadError && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="upload-error">{uploadError}</div>}
          {lastResult && (
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-2" data-testid="upload-success">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                {lastResult.duplicate
                  ? "Il file era già stato caricato in precedenza (stesso hash SHA-256): mostro il job esistente."
                  : "Job registrato. Espandi la riga per lanciare l'analisi."}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-[#F1F5F9]">
            <div className="text-xs text-slate-500">L&apos;upload salva il JSON per l&apos;analisi successiva.</div>
            <button type="button" disabled={!canUpload} onClick={handleUpload} className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-5 py-2.5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="btn-upload">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Caricamento…" : "Carica"}
            </button>
          </div>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Job registrati</div>
            <div className="font-display text-3xl text-[#1F2937]">{totals.jobs}</div>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Record totali</div>
            <div className="font-display text-3xl text-[#1F2937]">{totals.records}</div>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Record importati</div>
            <div className="font-display text-3xl text-[#1F2937]">{totals.imported}</div>
          </div>
        </section>

        {/* Jobs list */}
        <section className="space-y-3" data-testid="jobs-list">
          <h2 className="font-display font-bold text-xl text-[#1F2937]">Migrazioni recenti</h2>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : jobs.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E5E7EB] rounded-2xl p-10 text-center text-slate-500">
              Nessuna migrazione registrata. Carica il primo JSON per iniziare.
            </div>
          ) : (
            <div className="space-y-2">
              {jobs.map((j) => (
                <JobRow key={j.id} job={j}
                  expanded={expandedId === j.id}
                  onToggle={() => setExpandedId(expandedId === j.id ? null : j.id)}
                  onDelete={handleDelete}
                  onChanged={load}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default MigrazioneJson;
